<script lang="ts">
  import { Check, Hourglass } from "lucide-svelte";
  import { Button } from "../button";
  import Squircle from "$assets/icons/squircle.svelte";
  import * as Tooltip from "$ui/tooltip";

  interface Item {
    logo: string;
    name: string;
    url: string;
    inPreview?: boolean;
    id: string;
  }

  interface Props {
    items: Item[];
    onConnect: (item: Item) => void;
    onEdit?: (item: Item) => void;
    connected: string[];
    pending: string[];
  }

  const { items, onConnect, onEdit, connected, pending }: Props = $props();
</script>

<div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
  {#each items as item}
    {@const isConnected = connected.find((i) => i === item.name || i === item.id)}
    {@const isPending = pending.find((i) => i === item.name || i === item.id)}
    <div
      class="bg-white p-6 rounded-md shadow-sm border border-gray-200 group hover:bg-[#EDEEE6] transition ease-in-out duration-300 transform-gpu overflow-hidden"
    >
      <div class="flex flex-col gap-4">
        <div class="">
          <div class="relative w-16">
            <img src={item.logo} alt={item.name} class="w-16 h-16" />
            {#if isConnected}
              <Tooltip.Root openDelay={1000}>
                <div class="absolute top-0 right-0 group/badge hover:scale-125 transition shadow-lg">
                  <div class="relative">
                    <Tooltip.Trigger>
                      <div class="absolute -top-3 -right-3 w-8 h-8 fill-green-300">
                        <Squircle></Squircle>
                      </div>
                      <div class="absolute -top-1.5 -right-1.5 text-green-600">
                        <Check class="w-5 h-5 group-hover/badge:animate-pulse" />
                      </div>
                    </Tooltip.Trigger>
                  </div>
                </div>

                <Tooltip.Content class="flex flex-col gap-2 py-3" sideOffset={35}>
                  <div>The connection to {item.name} has been successfully created.</div>
                </Tooltip.Content>
              </Tooltip.Root>
            {:else if isPending}
              <Tooltip.Root openDelay={1000}>
                <div class="absolute top-0 right-0 group/badge hover:scale-125 transition shadow-lg">
                  <div class="relative">
                    <Tooltip.Trigger>
                      <div class="absolute -top-3 -right-3 w-8 h-8 fill-yellow-300">
                        <Squircle></Squircle>
                      </div>
                      <div class="absolute -top-1.5 -right-1.5 text-yellow-600">
                        <Hourglass class="w-5 h-5 group-hover/badge:animate-ease-out-spin animate-ease-in-spin" />
                      </div>
                    </Tooltip.Trigger>
                  </div>
                </div>
                <Tooltip.Content class="flex flex-col gap-2 py-3 max-w-lg" sideOffset={35}>
                  <div>
                    The connection to {item.name} has not been completed yet. An admin will need to authorize the connection.
                  </div>
                </Tooltip.Content>
              </Tooltip.Root>
            {/if}
          </div>
        </div>

        <div class="flex justify-between items-center transform-gpu">
          <div class="flex flex-col">
            <span class="text-lg font-semibold text-offblack">{item.name}</span>
            <span class="text-sm text-gray-500">{item.url}</span>
          </div>
          {#if isConnected}
            {#if onEdit}
              <Button
                class="transition ease-in-out duration-300 hover:bg-[#132c40] hover:text-white hover:border-[#132c40] text-black rounded-md bg-transparent border-2 border-gray-600"
                on:click={() => onEdit(item)}><span>Edit</span></Button
              >
            {:else}
              <div></div>
            {/if}
          {:else if isPending}
            <Button
              class="transition ease-in-out duration-300 hover:bg-[#132c40] hover:text-white hover:border-[#132c40] text-black rounded-md bg-transparent border-2 border-gray-600"
              on:click={() => onConnect(item)}><span>Edit</span></Button
            >
          {:else if item.inPreview}
            <Button
              class="transition ease-in-out duration-300 hover:bg-[#132c40] hover:text-white hover:border-[#132c40] text-black rounded-md bg-transparent border-2 border-gray-600"
              title="This connection is in preview. Contact support for more information."
            >
              <a href="mailto:support@redactive.ai">Contact Us</a>
            </Button>
          {:else}
            <Button
              class="transition ease-in-out duration-300 hover:bg-[#132c40] hover:text-white hover:border-[#132c40] text-black rounded-md bg-transparent border-2 border-gray-600"
              on:click={() => onConnect(item)}>Select</Button
            >
          {/if}
        </div>
      </div>
      {#if item.inPreview}
        <div
          class="absolute top-5 -right-11 py-0 bg-yellow-500 text-white font-medium px-12 rotate-45 shadow-md"
          title="This connection is in preview. Contact support for more information."
        >
          Preview
        </div>
      {/if}
    </div>
  {/each}
</div>
